/* eslint-disable max-lines-per-function */
import React, { useState, useEffect } from 'react';
import './blogs.scss';
import DataGridComponent from '../../Components/DataGrid/dataGrid';
import YearDropdown from './yearDropdown'; // Import the new component
import {
  BLOGS_TABLE_FIELD,
  BLOGS_TABLE_HEADER,
  BLOGS,
} from '../../Constants/strings';
import TotalSessionIcon from '../../Assets/images/svg/total_active_old.svg';
import UniqueAuthors from '../../Assets/images/svg/dar_unique_authors.svg';
import TotalBlogs from '../../Assets/images/svg/dar_total_blogs.svg';
import UseAxios from '../../Api/http/http';
import { getBlogs } from '../../Api/request/blogs';
import { formatDate } from '../StaffAugmentation/staffAugmentationUtils';

const Blogs = () => {
  const [rows, setRows] = useState([]);
  const [isMobileView, setIsMobileView] = useState(false);
  const [isLaptopView, setIsLaptopView] = useState(false);
  const [selectedYears, setSelectedYears] = useState([]);
  const [recentlySelected, setRecentlySelected] = useState(null); // Track the most recently selected year

  const currentYear = new Date().getFullYear(); // Get the current year
  const years = [currentYear, currentYear - 1, currentYear - 2]; // Generate the last three years

  const handleYearChange = (event) => {
    const {
      target: { value },
    } = event;
    setSelectedYears(typeof value === 'string' ? value.split(',') : value);
    getBlogsData(typeof value === 'string' ? value.split(',') : value);
    setRecentlySelected(value[value.length - 1]); // Update the most recently selected year
  };

  const cards = [
    {
      title: 'Unique authors',
      value: rows.totalUniqueUsers,
      icon: UniqueAuthors,
    },
    {
      title: 'Total blogs',
      value: rows.totalBlogs,
      icon: TotalSessionIcon,
    },
    {
      title: 'Total blogs (This month)',
      value: rows.totalBlogsPerMonth,
      icon: TotalBlogs,
    },
  ];

  useEffect(() => {
    getBlogsData([]);
    checkMobileView();
    window.addEventListener('resize', checkMobileView);
    return () => {
      window.removeEventListener('resize', checkMobileView);
    };
  }, []);

  const getBlogsData = (year) => { // API call to get staff augmentation list data
    UseAxios(getBlogs(year))
      .then((response) => {
        if (response?.status === 200) {
          setRows(response.data || []);
        } else {
          setRows([]);
        }
      })
      .catch(() => {
        setRows([]);
      });
  };

  const checkMobileView = () => {
    setIsMobileView(window.innerWidth < 1024);
    setIsLaptopView(window.innerWidth >= 1024 && window.innerWidth <= 2400);
  };

  const filteredRows = rows?.blogs
    ? rows.blogs.map((row, index) => ({
      ...row,
      id: row.id ? row.id : index + 1, // Add an auto-incremented id starting from 1
      status: row.status === '1' ? 'Live' : 'In Progress', // Map status to "Live" or "In Progress"
      createdDate: formatDate(row.createdDate), // Format createdDate
    }))
    : [];

  const getColumnWidth = (mobile, laptop, defaultWidth) => {
    if (isMobileView) {
      return mobile;
    } else if (isLaptopView) {
      return laptop;
    }
    return defaultWidth;
  };

  const getColumnFlex = (defaultFlex) => {
    if (!isMobileView && !isLaptopView) {
      return defaultFlex;
    }
    return undefined;
  };

  const columns = [
    {
      field: BLOGS_TABLE_FIELD.CREATED_DATE,
      headerName: BLOGS_TABLE_HEADER.CREATED_DATE,
      width: getColumnWidth(180, 200, 100),
      flex: getColumnFlex(0.7),
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: BLOGS_TABLE_FIELD.NAME,
      headerName: BLOGS_TABLE_HEADER.NAME,
      width: getColumnWidth(400, 400, 100),
      flex: getColumnFlex(0.7),
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: BLOGS_TABLE_FIELD.ASSET_TYPE,
      headerName: BLOGS_TABLE_HEADER.ASSET_TYPE,
      width: getColumnWidth(150, 150, 100),
      flex: getColumnFlex(0.7),
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: BLOGS_TABLE_FIELD.STATUS,
      headerName: BLOGS_TABLE_HEADER.STATUS,
      width: getColumnWidth(150, 150, 100),
      flex: getColumnFlex(0.7),
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: BLOGS_TABLE_FIELD.MONTH,
      headerName: BLOGS_TABLE_HEADER.MONTH,
      width: getColumnWidth(150, 150, 100),
      flex: getColumnFlex(0.7),
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: BLOGS_TABLE_FIELD.WEEK,
      headerName: BLOGS_TABLE_HEADER.WEEK,
      width: getColumnWidth(150, 150, 100),
      flex: getColumnFlex(0.7),
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: BLOGS_TABLE_FIELD.PUBLISH_DATE,
      headerName: BLOGS_TABLE_HEADER.PUBLISH_DATE,
      width: getColumnWidth(150, 150, 100),
      flex: getColumnFlex(0.7),
      sortable: false,
      disableColumnMenu: true,
      valueFormatter: (params) => formatDate(params),
    },
    {
      field: BLOGS_TABLE_FIELD.AUTHOR,
      headerName: BLOGS_TABLE_HEADER.AUTHOR,
      width: getColumnWidth(150, 150, 100),
      flex: getColumnFlex(0.7),
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: BLOGS_TABLE_FIELD.EMP_ID,
      headerName: BLOGS_TABLE_HEADER.EMP_ID,
      width: getColumnWidth(150, 200, 100),
      flex: getColumnFlex(0.7),
      sortable: false, // Enable sorting
      filterable: true, // Enable filtering
      disableColumnMenu: true,
    },
    {
      field: BLOGS_TABLE_FIELD.EMP_EMAIL,
      headerName: BLOGS_TABLE_HEADER.EMP_EMAIL,
      width: getColumnWidth(250, 250, 100),
      flex: getColumnFlex(0.7),
      sortable: false,
      disableColumnMenu: true,
    },
  ];

  const mobileColumns = [];
  const desktopColumns = [];

  if (isLaptopView || isMobileView) {
    mobileColumns.push(...columns);
  } else {
    desktopColumns.push(...columns);
  }

  const sortFilterColumns = [
    columns[0].field,
    columns[1].field,
    columns[2].field,
    columns[3].field,
    columns[4].field,
    columns[5].field,
    columns[6].field,
    columns[7].field,
    columns[8].field,
    columns[9].field,
  ];

  return (
    <div className="blogs-master-outer-layer">
      <div className="blogs-master">
        <div className="blogs-master-title">{BLOGS.BLOGS}</div>
        <div className="blogs-search-and-buttons">
          <div className="blogs-section">
            <div className="blogs-list">
              {cards.map((vertical, index) => (
                <React.Fragment key={vertical?.title}>
                  <div className="blogs-item">
                    <img src={vertical.icon} alt="Target Icon" className="blogs-item-icon" />
                    <div className="blogs-item-details">
                      <div className="blogs-item-title">{vertical.title}</div>
                      <div className="blogs-item-value">{vertical.value}</div>
                    </div>
                  </div>
                  {/* Render YearDropdown next to the last card only for mobile view */}
                  {isMobileView && index === cards.length - 1 && (
                    <div className="blogs-year-dropdown-container">
                      <YearDropdown
                        years={years}
                        selectedYears={selectedYears}
                        handleYearChange={handleYearChange}
                        recentlySelected={recentlySelected}
                      />
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
          {!isMobileView && (
            <YearDropdown
              years={years}
              selectedYears={selectedYears}
              handleYearChange={handleYearChange}
              recentlySelected={recentlySelected}
            />
          )}
        </div>
        <div className="blogs-data-grid-container">
          {filteredRows ? (
            <DataGridComponent
              dataRows={filteredRows}
              dataColumns={
                isMobileView || isLaptopView ? mobileColumns : desktopColumns
              }
              isFilteredRows={filteredRows}
              sortFilterColumns={sortFilterColumns}
              pageSize={10}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default Blogs;
